import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { Bell, CheckCircle, DeviceMobile } from "@phosphor-icons/react";

export default function Notifications() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unread

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(prev => prev.map(i => i.id === id ? { ...i, read: true } : i));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markAllRead = async () => {
    const unread = items.filter(i => !i.read);
    for (const n of unread) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    load();
  };

  const filtered = filter === "unread" ? items.filter(i => !i.read) : items;
  const unreadCount = items.filter(i => !i.read).length;

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Communication</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">
            Notifications {unreadCount > 0 && <span className="text-accent">· {unreadCount} non lue{unreadCount > 1 ? "s" : ""}</span>}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            SMS et notifications in-app envoyés aux parents (mode MOCKÉ — les messages sont journalisés côté serveur).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")} data-testid="btn-notif-all">Toutes ({items.length})</Button>
          <Button variant={filter === "unread" ? "default" : "outline"} size="sm" onClick={() => setFilter("unread")} data-testid="btn-notif-unread">Non lues ({unreadCount})</Button>
          {unreadCount > 0 && <Button size="sm" onClick={markAllRead} data-testid="btn-notif-mark-all"><CheckCircle size={14} className="mr-1" /> Tout marquer lu</Button>}
        </div>
      </header>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <Bell size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
          <div className="font-heading font-semibold text-lg">Aucune notification</div>
          <p className="text-sm text-muted-foreground mt-2">
            Les SMS aux parents sont générés automatiquement lors des paiements et des absences.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(n => (
            <Card
              key={n.id}
              className={`p-4 border-l-4 ${n.read ? "border-l-border opacity-70" : "border-l-accent"} card-hover cursor-pointer`}
              onClick={() => !n.read && markRead(n.id)}
              data-testid={`notif-${n.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${n.read ? "bg-muted text-muted-foreground" : "bg-accent/10 text-accent"}`}>
                  <DeviceMobile size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-semibold text-sm">{n.subject}</div>
                      <div className="text-xs text-muted-foreground">
                        <span className="uppercase tracking-wider font-semibold">{n.channel}</span>
                        · statut <span className="font-mono">{n.status}</span>
                      </div>
                    </div>
                    <div className="text-[10px] text-muted-foreground whitespace-nowrap">{formatDateTime(n.created_at)}</div>
                  </div>
                  <div className="text-sm mt-2 leading-relaxed">{n.message}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
